/**
 * A bet form built from the schema the engine published.
 *
 * Every other game in this client has a scene written for it by hand, which
 * means a game the platform can route is a game this client cannot draw. The
 * session already carries the engine's own JSON Schema for its bet and its
 * actions — that is what the platform validates against — so it is also
 * enough to put a control on the screen for each field, with the bounds the
 * engine said it accepts.
 *
 * This is deliberately not a JSON Schema implementation. It recognises the
 * handful of shapes a bet is made of and falls back to a raw JSON box for
 * anything else; the server's validator is the one that decides, and a form
 * that guessed wrong produces a 400 with a message rather than a bad bet.
 */

export interface JsonSchema {
  type?: string;
  enum?: Array<string | number>;
  const?: unknown;
  minimum?: number;
  maximum?: number;
  minItems?: number;
  maxItems?: number;
  pattern?: string;
  default?: unknown;
  items?: JsonSchema;
  properties?: Record<string, JsonSchema>;
  required?: string[];
  anyOf?: JsonSchema[];
  oneOf?: JsonSchema[];
  [key: string]: unknown;
}

/** A rendered control: its DOM, its current value, and whether it is usable. */
export interface Field {
  element: HTMLElement;
  /** `undefined` when the field is empty and optional. */
  value(): unknown;
  valid(): boolean;
}

/**
 * The stake is not rendered from the schema.
 *
 * Every bet has one and every engine declares it the same way, but it is the
 * one field the platform does know about: it is bounded by the operator's
 * limits, not the game's, and the shell already offers those as bet levels.
 * Drawing it twice would let a player pick an amount the operator refuses.
 */
export const AMOUNT_FIELD = 'amount';

export function betSchemaOf(schema: unknown): JsonSchema | null {
  return propertyOf(schema, 'bet');
}

export function actionSchemaOf(schema: unknown): JsonSchema | null {
  return propertyOf(schema, 'action');
}

function propertyOf(schema: unknown, key: string): JsonSchema | null {
  const properties = (schema as JsonSchema | null)?.properties;
  const found = properties?.[key];
  return found && typeof found === 'object' ? found : null;
}

/**
 * The fields of an object schema, in the order the engine declared them,
 * with the stake left out.
 */
export function fieldsOf(schema: JsonSchema | null): Array<[string, JsonSchema]> {
  if (!schema?.properties) return [];
  return Object.entries(schema.properties).filter(([name]) => name !== AMOUNT_FIELD);
}

export function isRequired(schema: JsonSchema | null, name: string): boolean {
  return schema?.required?.includes(name) ?? false;
}

/** The moves this game offers, from `action.type`. Empty if it has none. */
export function actionTypesOf(schema: JsonSchema | null): string[] {
  const type = schema?.properties?.type;
  const values = type?.enum ?? (type?.const !== undefined ? [type.const] : []);
  return values.filter((value): value is string => typeof value === 'string');
}

export interface FieldOptions {
  /**
   * Draw a bounded integer as one button per value rather than a slider.
   *
   * The two read very differently even though the schema is the same. A
   * count — how many bombs, how many rows — is a quantity and belongs on a
   * slider. A position — which tile, which lane — is a place on the board,
   * and a player picks it by pointing at it. Only the caller knows which
   * kind of number it asked for.
   */
  grid?: boolean;
}

/**
 * Renders one field.
 *
 * `required` decides what an empty control means: on a required field the
 * form is not yet valid, on an optional one the key is simply left out and
 * the engine uses its own default.
 */
export function renderField(
  name: string,
  schema: JsonSchema,
  required: boolean,
  options: FieldOptions = {},
): Field {
  const wrap = document.createElement('div');
  wrap.className = 'gen-field';

  const field = control(schema, required, options);

  // The caption only binds to the control when the control is a single input.
  // A segmented row or a pick grid is a group of buttons, and wrapping those
  // in a label makes every click land on the first one as well as the one
  // that was pressed.
  const caption = document.createElement('label');
  caption.className = 'gen-field-label';
  caption.textContent = labelOf(name);
  if (field.element instanceof HTMLInputElement || field.element instanceof HTMLTextAreaElement) {
    field.element.id = `gen-${name}-${nextFieldId++}`;
    caption.htmlFor = field.element.id;
  }

  wrap.append(caption, field.element);

  return { element: wrap, value: field.value, valid: field.valid };
}

let nextFieldId = 0;

/** How many buttons a grid of numbers may have before it is a slider again. */
const GRID_LIMIT = 100;

function control(schema: JsonSchema, required: boolean, options: FieldOptions): Field {
  const choices = schema.enum ?? enumOf(schema);
  if (choices) return segmented(choices, schema.default, required);

  const numeric = numericOf(schema);
  if (numeric) {
    if (
      options.grid &&
      numeric.integer &&
      numeric.min !== undefined &&
      numeric.max !== undefined &&
      numeric.max - numeric.min < GRID_LIMIT
    ) {
      return chooser(numeric.min, numeric.max, required);
    }
    return numberField(numeric, schema.default, required);
  }

  if (schema.type === 'boolean') return toggle(schema.default === true);

  if (schema.type === 'array' && schema.items) return picker(schema);

  if (schema.type === 'string') return textField(schema, required);

  return jsonField(schema, required);
}

/**
 * A union is how a schema library writes "a number or the string of one".
 * Whichever branch is numeric or enumerated is the one worth drawing.
 */
function branches(schema: JsonSchema): JsonSchema[] {
  return schema.anyOf ?? schema.oneOf ?? [];
}

function enumOf(schema: JsonSchema): Array<string | number> | null {
  for (const branch of branches(schema)) {
    if (branch.enum) return branch.enum;
  }
  return null;
}

interface Numeric {
  integer: boolean;
  min?: number;
  max?: number;
}

function numericOf(schema: JsonSchema): Numeric | null {
  const candidates = [schema, ...branches(schema)];
  for (const candidate of candidates) {
    if (candidate.type === 'integer' || candidate.type === 'number') {
      return {
        integer: candidate.type === 'integer',
        min: candidate.minimum,
        max: candidate.maximum,
      };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Controls
// ---------------------------------------------------------------------------

function segmented(options: Array<string | number>, fallback: unknown, required: boolean): Field {
  const row = document.createElement('div');
  row.className = 'gen-segmented';

  let selected: string | number | undefined =
    fallback !== undefined && options.includes(fallback as string | number)
      ? (fallback as string | number)
      : required
        ? options[0]
        : undefined;

  const buttons = options.map((option) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'gen-seg';
    button.textContent = String(option);
    button.addEventListener('click', () => {
      selected = option;
      paint();
    });
    row.append(button);
    return { option, button };
  });

  function paint(): void {
    for (const { option, button } of buttons) {
      button.classList.toggle('on', option === selected);
    }
  }
  paint();

  return {
    element: row,
    value: () => selected,
    valid: () => !required || selected !== undefined,
  };
}

/**
 * A number the engine bounded, as a slider and a box together: the slider is
 * how a bomb count or a row count is actually chosen, the box is how a exact
 * value is typed when it matters.
 */
function numberField(numeric: Numeric, fallback: unknown, required: boolean): Field {
  const row = document.createElement('div');
  row.className = 'gen-number';

  const input = document.createElement('input');
  input.type = 'number';
  input.className = 'gen-input';
  if (numeric.min !== undefined) input.min = String(numeric.min);
  if (numeric.max !== undefined) input.max = String(numeric.max);
  input.step = numeric.integer ? '1' : 'any';

  const start = typeof fallback === 'number' ? fallback : required ? (numeric.min ?? 0) : undefined;
  if (start !== undefined) input.value = String(start);

  // A slider needs both ends. Without them there is nothing to slide along,
  // and an unbounded value is typed anyway.
  const bounded =
    numeric.min !== undefined && numeric.max !== undefined && numeric.max - numeric.min <= 1000;

  if (bounded) {
    const slider = document.createElement('input');
    slider.type = 'range';
    slider.className = 'gen-slider';
    slider.min = String(numeric.min);
    slider.max = String(numeric.max);
    slider.step = numeric.integer ? '1' : 'any';
    slider.value = input.value || String(numeric.min);
    slider.addEventListener('input', () => {
      input.value = slider.value;
    });
    input.addEventListener('input', () => {
      if (input.value !== '') slider.value = input.value;
    });
    row.append(slider);
  }

  row.append(input);

  const read = (): number | undefined => {
    if (input.value.trim() === '') return undefined;
    const value = Number(input.value);
    return Number.isFinite(value) ? value : undefined;
  };

  return {
    element: row,
    value: read,
    valid: () => {
      const value = read();
      if (value === undefined) return !required;
      if (numeric.integer && !Number.isInteger(value)) return false;
      if (numeric.min !== undefined && value < numeric.min) return false;
      if (numeric.max !== undefined && value > numeric.max) return false;
      return true;
    },
  };
}

/** One button per value in an integer range, of which one may be chosen. */
function chooser(min: number, max: number, required: boolean): Field {
  let selected: number | undefined;

  const grid = document.createElement('div');
  grid.className = 'gen-grid';

  const buttons: HTMLButtonElement[] = [];
  for (let value = min; value <= max; value++) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'gen-cell';
    button.textContent = String(value);
    button.addEventListener('click', () => {
      selected = value;
      paint();
    });
    grid.append(button);
    buttons.push(button);
  }

  function paint(): void {
    buttons.forEach((button, index) => button.classList.toggle('on', min + index === selected));
  }

  return {
    element: grid,
    value: () => selected,
    valid: () => !required || selected !== undefined,
  };
}

function toggle(initial: boolean): Field {
  const input = document.createElement('input');
  input.type = 'checkbox';
  input.className = 'gen-toggle';
  input.checked = initial;
  return { element: input, value: () => input.checked, valid: () => true };
}

/**
 * A grid of numbers to pick from — keno's board, and anything else shaped
 * like it. Drawn as a grid rather than a text field because "pick ten of
 * forty" is a thing you click, and because the count bounds are then
 * enforced by the buttons going dead rather than by a rejected bet.
 */
function picker(schema: JsonSchema): Field {
  const item = numericOf(schema.items ?? {});
  const min = schema.minItems ?? 0;
  const max = schema.maxItems;

  // Without an integer range there is nothing to lay out, so the value is
  // typed as JSON and the server decides.
  if (!item || item.min === undefined || item.max === undefined || !item.integer) {
    return jsonField(schema, min > 0);
  }

  const chosen = new Set<number>();
  const grid = document.createElement('div');
  grid.className = 'gen-grid';

  const buttons: HTMLButtonElement[] = [];
  for (let value = item.min; value <= item.max; value++) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'gen-cell';
    button.textContent = String(value);
    button.addEventListener('click', () => {
      if (chosen.has(value)) chosen.delete(value);
      else if (max === undefined || chosen.size < max) chosen.add(value);
      paint();
    });
    grid.append(button);
    buttons.push(button);
  }

  const count = document.createElement('span');
  count.className = 'gen-count';

  function paint(): void {
    buttons.forEach((button, index) => {
      const value = item!.min! + index;
      const on = chosen.has(value);
      button.classList.toggle('on', on);
      button.disabled = !on && max !== undefined && chosen.size >= max;
    });
    count.textContent = max === undefined ? `${chosen.size} picked` : `${chosen.size} / ${max}`;
  }
  paint();

  const wrap = document.createElement('div');
  wrap.className = 'gen-picker';
  wrap.append(grid, count);

  return {
    element: wrap,
    value: () => [...chosen].sort((a, b) => a - b),
    valid: () => chosen.size >= min && (max === undefined || chosen.size <= max),
  };
}

function textField(schema: JsonSchema, required: boolean): Field {
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'gen-input';
  if (typeof schema.default === 'string') input.value = schema.default;

  // The engine's own pattern, so a malformed value is refused before it is
  // sent rather than after.
  let pattern: RegExp | null = null;
  if (schema.pattern) {
    try {
      pattern = new RegExp(schema.pattern);
    } catch {
      pattern = null;
    }
  }

  return {
    element: input,
    value: () => (input.value === '' ? undefined : input.value),
    valid: () => {
      if (input.value === '') return !required;
      return pattern ? pattern.test(input.value) : true;
    },
  };
}

/** Anything this form does not recognise, typed as JSON and sent as given. */
function jsonField(schema: JsonSchema, required: boolean): Field {
  const box = document.createElement('textarea');
  box.className = 'gen-json';
  box.rows = 2;
  box.placeholder = schema.type === 'array' ? '[]' : '{}';
  if (schema.default !== undefined) box.value = JSON.stringify(schema.default);

  const parse = (): { ok: boolean; value?: unknown } => {
    if (box.value.trim() === '') return { ok: !required };
    try {
      return { ok: true, value: JSON.parse(box.value) };
    } catch {
      return { ok: false };
    }
  };

  return {
    element: box,
    value: () => parse().value,
    valid: () => parse().ok,
  };
}

/** `minesCount` reads better as "Mines count" and costs nothing to do. */
function labelOf(name: string): string {
  const spaced = name.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
